/**
 * API Service Layer
 *
 * Handles communication with the Trusscore Estimator API backend.
 *
 * Products:  served by the Node Express API (localhost:3001)
 * Estimates: served by PHP endpoints (same origin, /api/estimates.php)
 */

import { Product, GuestEstimateSubmission, SavedEstimate, Comment, CustomFieldColumnConfig, OutputLayoutConfig, CustomTableDef } from '../types';
import type { ActionButtonConfig } from '../config/actionButtons';
import { type CapturePolicy, normalizeCapturePolicy } from '../config/capturePolicy';
import type { GeoCountry } from '../data/regions';
import type { AppSettings } from '../context/AppSettingsContext';
import { type LocaleDef, normalizeLocales, SOURCE_LOCALE } from '../config/locales';
import { type OutputSection, normalizeOutputSections } from '../config/outputSections';
import { withLocale, activeLocale, tr } from '../i18n/standalone';

/**
 * PHP API base URL — uses same origin by default (works in both
 * MAMP local dev and production Virtualmin).
 * Override with REACT_APP_API_URL if needed.
 */
import type { RenderStatus } from '../components/results/render/types';

const API_URL = process.env.REACT_APP_API_URL || '';

// ============================================================================
// PRODUCT ENDPOINTS
// ============================================================================

/**
 * Fetch products with retailer-specific pricing.
 *
 * @param retailerId - Retailer ID (default: 1 = Trusscore MSRP)
 * @returns Array of Product objects matching the frontend Product interface
 */
export interface ProductsPayload {
  products: Product[];
  /** Admin-configured custom-field column layout for this retailer context. */
  fieldConfig: CustomFieldColumnConfig[];
  /** Resolved output column layouts (BOM / placement) for this retailer. */
  outputLayout: OutputLayoutConfig | null;
  /** Custom output tables (scan sheets) for this retailer. */
  customTables: CustomTableDef[];
  /** Configurable "Next Steps" action-button bar for this retailer (universal→retailer). */
  actionButtons: ActionButtonConfig[];
  /** All active fields (key + label) for barcode/token sources, even non-output. */
  availableFields: { fieldKey: string; label: string }[];
  /**
   * How much this retailer asks a guest for before saving, and whether the
   * detailed materials list is gated behind that form. Rides with pricing rather
   * than the branded-portal payload so it also reaches non-partner retailers.
   */
  capturePolicy: CapturePolicy;
  /** false suppresses the accounts UI on this retailer's portal. */
  accountsEnabled: boolean;
  /** Location maps: which map (if any) renders inline below the BOM for this retailer. */
  locationMaps: { inlineMapId: number | null };
  /** Output footer sections (rich text after the last tables, before the disclaimer), resolved + ordered. */
  outputSections: OutputSection[];
}

/**
 * Fetch products + custom-field column config for a retailer context.
 */
export async function fetchProductsPayload(retailerId: number = 1): Promise<ProductsPayload> {
  // One `locale` covers six of the eight translatable entities: this response
  // carries products, fieldConfig, outputLayout, customTables, actionButtons
  // and availableFields. Callers must re-fetch on a language change —
  // ProductsBootGate lists the locale as an effect dependency.
  const url = withLocale(`${API_URL}/api/products.php?pricing=1&retailer_id=${retailerId}`);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  return {
    products: (data.products ?? []) as Product[],
    fieldConfig: (data.fieldConfig ?? []) as CustomFieldColumnConfig[],
    outputLayout: (data.outputLayout ?? null) as OutputLayoutConfig | null,
    customTables: (data.customTables ?? []) as CustomTableDef[],
    actionButtons: (data.actionButtons ?? []) as ActionButtonConfig[],
    availableFields: (data.availableFields ?? []) as { fieldKey: string; label: string }[],
    capturePolicy: normalizeCapturePolicy(data.capturePolicy),
    // Absent on an un-migrated server → accounts stay on, i.e. today's behaviour.
    accountsEnabled: data.accountsEnabled !== false,
    locationMaps: {
      inlineMapId: typeof data?.locationMaps?.inlineMapId === 'number' && data.locationMaps.inlineMapId > 0 ? data.locationMaps.inlineMapId : null,
    },
    outputSections: normalizeOutputSections(data.outputSections),
  };
}

export async function fetchProducts(retailerId: number = 1): Promise<Product[]> {
  const { products } = await fetchProductsPayload(retailerId);
  return products;
}

// ============================================================================
// ADDRESS AUTOCOMPLETE (server-side proxy — provider/key live server-side)
// ============================================================================

export interface AddressSuggestion {
  id: string;
  label: string;
  /**
   * Present when the provider returned address components with the suggestion
   * (every provider except Google) — picking it needs no second round-trip.
   */
  detail?: AddressDetail;
}

export interface AddressDetail {
  addressOne: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  countryCode: string;
  /** Whether the resolved address included a street/building number. */
  hasStreetNumber?: boolean;
  /** Coordinates, when the provider supplied them. */
  latitude?: number;
  longitude?: number;
}

// ---- Geocoding registry (public/api/geocode.php) ---------------------------------------

/** The 13 canonical address components every provider normalises to. */
export interface GeoComponents {
  house_number: string;
  street: string;
  unit: string;
  city: string;
  county: string;
  region: string;
  region_code: string;
  postal_code: string;
  country: string;
  country_code: string;
  latitude: number | '';
  longitude: number | '';
  formatted: string;
}

export interface GeoPrediction {
  id: string;
  label: string;
  /** Empty object/array for providers that need a details call (Google). */
  components: Partial<GeoComponents> | Record<string, never> | never[];
}

export interface GeocodeResponse {
  provider: string;
  predictions: GeoPrediction[];
  cached?: boolean;
  warnings?: string[];
  /** 'not_configured' when the provider needs a key and none is stored. */
  error?: string;
}

export interface GeocodeParams {
  q: string;
  provider?: string;
  purpose?: 'address' | 'map';
  countries?: string[];
  country?: string;
  lat?: number;
  lng?: number;
  bbox?: [number, number, number, number];
  limit?: number;
  lang?: string;
  session?: string;
  regions?: string[];
  biasIp?: boolean;
  types?: 'address';
  signal?: AbortSignal;
}

export interface GeoProviderMeta {
  name: string;
  label: string;
  keyless: boolean;
  freeTier: string;
  attribution: false | string;
  supportsDetails: boolean;
  needsSession: boolean;
  configured: boolean;
  keyHint?: string;
  endpointSetting?: string;
  refused?: string;
}

function geocodeQuery(p: GeocodeParams): string {
  const sp = new URLSearchParams();
  sp.set('action', 'autocomplete');
  sp.set('q', p.q);
  if (p.provider) sp.set('provider', p.provider);
  if (p.purpose) sp.set('purpose', p.purpose);
  if (p.countries?.length) sp.set('countries', p.countries.join(','));
  if (p.country) sp.set('country', p.country);
  if (p.lat !== undefined && p.lng !== undefined) { sp.set('lat', String(p.lat)); sp.set('lng', String(p.lng)); }
  if (p.bbox) sp.set('bbox', p.bbox.join(','));
  if (p.limit) sp.set('limit', String(p.limit));
  if (p.lang) sp.set('lang', p.lang);
  if (p.session) sp.set('session', p.session);
  if (p.regions?.length) sp.set('regions', p.regions.join(','));
  if (p.biasIp === false) sp.set('biasIp', '0');
  if (p.types) sp.set('types', p.types);
  return sp.toString();
}

/** Type-ahead predictions from the shared geocoding registry. Never throws. */
export async function fetchGeocode(p: GeocodeParams): Promise<GeocodeResponse> {
  const provider = p.provider ?? '';
  try {
    const res = await fetch(`${API_URL}/api/geocode.php?${geocodeQuery(p)}`, {
      signal: p.signal ?? AbortSignal.timeout(6000),
    });
    if (!res.ok) return { provider, predictions: [], error: `http_${res.status}` };
    const data = await res.json();
    return {
      provider: typeof data?.provider === 'string' ? data.provider : provider,
      predictions: Array.isArray(data?.predictions) ? (data.predictions as GeoPrediction[]) : [],
      cached: !!data?.cached,
      warnings: Array.isArray(data?.warnings) ? data.warnings : [],
      error: typeof data?.error === 'string' ? data.error : undefined,
    };
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') return { provider, predictions: [], error: 'aborted' };
    return { provider, predictions: [], error: 'network' };
  }
}

/** Full components for one prediction id (Google). Null when unavailable. */
export async function fetchGeocodeDetails(id: string, provider?: string, session?: string): Promise<GeoComponents | null> {
  try {
    const sp = new URLSearchParams({ action: 'details', id });
    if (provider) sp.set('provider', provider);
    if (session) sp.set('session', session);
    const res = await fetch(`${API_URL}/api/geocode.php?${sp.toString()}`, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.components && typeof data.components === 'object' ? (data.components as GeoComponents) : null;
  } catch {
    return null;
  }
}

/** Provider roster + which ones are configured (admin settings page). */
export async function fetchGeoProviders(): Promise<{ default: string; mapDefault: string; providers: GeoProviderMeta[] } | null> {
  try {
    const res = await fetch(`${API_URL}/api/geocode.php?action=providers`, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data?.providers) ? data : null;
  } catch {
    return null;
  }
}

/** Canonical components → the wizard's flat address shape (mirror of geoLegacyAddress in PHP). */
export function geoComponentsToDetail(c: Partial<GeoComponents>): AddressDetail {
  const house = (c.house_number ?? '').trim();
  const street = `${house} ${(c.street ?? '').trim()}`.trim();
  const lat = typeof c.latitude === 'number' ? c.latitude : undefined;
  const lng = typeof c.longitude === 'number' ? c.longitude : undefined;
  return {
    addressOne: street !== '' ? street : (c.formatted ?? ''),
    city: c.city ?? '',
    region: c.region ?? '',
    postalCode: c.postal_code ?? '',
    country: c.country ?? '',
    countryCode: c.country_code ?? '',
    hasStreetNumber: house !== '',
    latitude: lat,
    longitude: lng,
  };
}

function geoComponentsUsable(c: unknown): c is Partial<GeoComponents> {
  if (!c || typeof c !== 'object' || Array.isArray(c)) return false;
  const o = c as Partial<GeoComponents>;
  return (o.formatted ?? '') !== '' || (o.city ?? '') !== '';
}

/**
 * Typeahead suggestions for an address query. Empty array on any failure.
 * `session` is an opaque per-search token (Google Places session billing) —
 * pass the same token across a search's suggestion calls + the final detail
 * lookup so they bill as one session. Ignored by providers that don't use it.
 */
export async function fetchAddressSuggestions(q: string, session?: string): Promise<AddressSuggestion[]> {
  const { suggestions } = await fetchAddressSuggestionsWithProvider(q, session);
  return suggestions;
}

/**
 * Same as fetchAddressSuggestions, plus which provider answered — the wizard needs it to
 * route the details call and to show LocationIQ's mandatory attribution.
 */
export async function fetchAddressSuggestionsWithProvider(q: string, session?: string): Promise<{ provider: string; suggestions: AddressSuggestion[] }> {
  const r = await fetchGeocode({ q, purpose: 'address', session, limit: 6, types: 'address' });
  const suggestions: AddressSuggestion[] = [];
  for (const p of r.predictions) {
    if (!p.label) continue;
    const s: AddressSuggestion = { id: String(p.id ?? ''), label: p.label };
    if (geoComponentsUsable(p.components)) s.detail = geoComponentsToDetail(p.components);
    suggestions.push(s);
  }
  return { provider: r.provider, suggestions };
}

/** Resolve a chosen suggestion id to a structured address. Null on failure. */
export async function fetchAddressDetail(id: string, session?: string, provider?: string): Promise<AddressDetail | null> {
  const c = await fetchGeocodeDetails(id, provider, session);
  return c ? geoComponentsToDetail(c) : null;
}

/**
 * Fetch the admin-managed country/region list for the guest-estimate form.
 * Returns null on any failure (offline / pre-migrate / empty) so callers fall
 * back to the hardcoded list in src/data/regions.ts.
 */
export async function fetchGeoCountries(): Promise<GeoCountry[] | null> {
  try {
    const res = await fetch(withLocale(`${API_URL}/api/geo.php`), { signal: AbortSignal.timeout(4000) });
    if (!res.ok) return null;
    const data = await res.json();
    const list = data?.countries;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.map((c: { name: string; nameEn?: string; isoCode?: string; regionLabel?: string; regions?: { name: string; nameEn?: string; code?: string }[] }) => ({
      name: String(c.name),
      // Falls back to the display name for a server that predates nameEn —
      // which is correct there, since it was serving English anyway.
      nameEn: String(c.nameEn ?? c.name),
      isoCode: String(c.isoCode ?? ''),
      regionLabel: String(c.regionLabel || 'Region'),
      regions: Array.isArray(c.regions)
        ? c.regions.map((r) => ({
            value: String(r.name),
            valueEn: String(r.nameEn ?? r.name),
            code: String(r.code || r.name),
          }))
        : [],
    }));
  } catch {
    return null;
  }
}

/**
 * Fetch the active locale registry. Returns null on any failure (offline /
 * pre-migrate / empty) so callers fall back to BUILTIN_LOCALES in
 * src/config/locales.ts — the app must never be language-less.
 */
export async function fetchLocales(): Promise<{ locales: LocaleDef[]; default: string } | null> {
  try {
    const res = await fetch(`${API_URL}/api/locales.php`, { signal: AbortSignal.timeout(4000) });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data?.locales) || data.locales.length === 0) return null;
    return {
      locales: normalizeLocales(data.locales),
      default: typeof data.default === 'string' && data.default ? data.default : SOURCE_LOCALE,
    };
  } catch {
    return null;
  }
}

/**
 * Fetch the cheap "version token" for a retailer's product payload. Changes
 * whenever any hydrated product data (products, GS1 codes, pricing, custom
 * fields, layouts, custom tables) changes. Used to decide whether a full
 * rehydrate is needed before showing an estimate — see maybeRefreshProducts().
 *
 * @returns the opaque version string, or null if the endpoint is unreachable
 *          (offline / not yet deployed) — callers treat null as "don't refresh".
 */
export async function fetchProductsVersion(retailerId: number = 1): Promise<string | null> {
  try {
    const response = await fetch(
      // The locale partitions the token — two languages are two payloads and
      // must never share a version, or a switch would leave the client on a
      // cached payload whose token still matched.
      withLocale(`${API_URL}/api/products-version.php?retailer_id=${retailerId}`),
      // Bounded so a flaky/offline network can't stall the estimate that's
      // waiting on this check before it renders.
      { cache: 'no-store', signal: AbortSignal.timeout(2500) },
    );
    if (!response.ok) return null;
    const data = await response.json();
    return (data?.version as string) ?? null;
  } catch {
    return null;
  }
}

/**
 * Health check — verify the API and database are reachable.
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/api/health.php`);
    return response.ok;
  } catch {
    return false;
  }
}

// ============================================================================
// ESTIMATE ENDPOINTS
// ============================================================================

/**
 * Payload returned alongside the save response when the `pardotDebug` admin
 * setting is enabled. Mirrors the shape built server-side so the browser
 * console can show exactly what would be POSTed to the Pardot form handler.
 */
export interface PardotDebugInfo {
  enabled: boolean;
  urlConfigured: boolean;
  payload: Record<string, string>;
}

/** What a save hands back for an estimate made in the 3D designer: the preview picture's URL and the render job's upload token. */
export interface RenderSaveExtras {
  previewUrl?: string | null;
  render?: { jobId: number; version: number; uploadToken: string; expiresAt: string } | null;
}

export interface GuestEstimateSaveResponse extends RenderSaveExtras {
  guid: string;
  message: string;
  pardotDebug?: PardotDebugInfo;
}

/**
 * The room's export (the .glb and its render manifest) for the render job of
 * a saved estimate: multipart to render-jobs.php, with the upload token the
 * save handed back (public/api/render-jobs.php `submit`).
 */
/**
 * A saved estimate's render job: queue position, progress and the pictures in
 * so far (render-jobs.php?action=status). 404: no such estimate; 410: deleted.
 */
export async function fetchRenderStatus(guid: string, signal?: AbortSignal): Promise<RenderStatus> {
  const res = await fetch(`${API_URL}/api/render-jobs.php?action=status&guid=${encodeURIComponent(guid)}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    signal,
  });
  if (!res.ok) {
    const err = new Error(`render status: HTTP ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<RenderStatus>;
}

export async function uploadRenderAsset(guid: string, token: string, asset: { glb: ArrayBuffer; manifest: unknown }): Promise<void> {
  const form = new FormData();
  form.append('guid', guid);
  form.append('token', token);
  form.append('manifest', new Blob([JSON.stringify(asset.manifest)], { type: 'application/json' }), 'room.render.json');
  form.append('glb', new Blob([asset.glb], { type: 'model/gltf-binary' }), 'room.glb');
  const res = await fetch(`${API_URL}/api/render-jobs.php?action=submit`, { method: 'POST', body: form, credentials: 'same-origin' });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Upload failed (${res.status})`);
  }
}

/**
 * Submit a guest estimate.
 * Saves to database, triggers Turnstile verification, and optional Pardot submission.
 *
 * @returns Object with the generated GUID and (when debug enabled) Pardot payload info.
 */
export async function submitGuestEstimate(
  submission: GuestEstimateSubmission
): Promise<GuestEstimateSaveResponse> {
  const response = await fetch(`${API_URL}/api/estimates.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Stamped here rather than at each call site: the language is global state,
    // it is the same answer for every caller, and a save path added later would
    // otherwise silently store an unmarked estimate. Two modals submit through
    // here today, and neither has to remember.
    body: JSON.stringify({ ...submission, locale: activeLocale() }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to save estimate');
  }

  return data;
}

/**
 * Fetch application-wide settings configured from the admin Settings page.
 * Server merges any missing keys with its defaults, so the response always
 * contains every known setting.
 */
export async function fetchAppSettings(): Promise<AppSettings> {
  const response = await fetch(`${API_URL}/api/settings.php`, {
    credentials: 'include',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `Failed to load settings (${response.status})`);
  }
  return data.settings as AppSettings;
}

/**
 * Patch one or more application-wide settings. Returns the merged server
 * state (not just the patch) so the caller can trust it as source of truth.
 */
export async function updateAppSettings(patch: Partial<AppSettings>): Promise<AppSettings> {
  const response = await fetch(`${API_URL}/api/settings.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings: patch }),
  });
  const data = await response.json();
  if (!response.ok) {
    // Prefer the server's human-readable `message` (e.g. the SMTP prerequisite for
    // native auth) over the machine `error` code, which reads as gibberish in a toast.
    const msg = data.message || data.error || `Failed to save settings (${response.status})`;
    const err = new Error(msg) as Error & { fields?: string[] };
    if (Array.isArray(data.fields)) err.fields = data.fields;
    throw err;
  }
  return data.settings as AppSettings;
}

// ---------------------------------------------------------------------------
// Native in-app auth (optional flow behind the nativeAuthEnabled toggle).
// Server-side BFF — the browser never receives tokens.
// ---------------------------------------------------------------------------

/**
 * Fetch a session-bound CSRF token for authenticated writes.
 *
 * Memoized: the token is stable for the life of the PHP session (it survives the
 * session-id rotation on login), and csrfFetch() is on the hot path for the
 * background estimate auto-save — re-fetching per write would double its requests.
 * csrfFetch() drops the cache and retries once on a 403, so a replaced session
 * self-heals; logout also calls resetAuthCsrf().
 */
let authCsrfCache: Promise<string> | null = null;

export async function fetchAuthCsrf(): Promise<string> {
  if (!authCsrfCache) {
    authCsrfCache = (async () => {
      const res = await fetch(`${API_URL}/api/auth-csrf.php`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.csrf) throw new Error(tr('auth:errors.sessionStart'));
      return data.csrf as string;
    })().catch((err) => {
      authCsrfCache = null; // don't cache a failure
      throw err;
    });
  }
  return authCsrfCache;
}

/** Drop the cached CSRF token — the session it was bound to is gone (logout). */
export function resetAuthCsrf(): void {
  authCsrfCache = null;
}

/**
 * fetch() for a CSRF-protected, cookie-authenticated call. Sends the session
 * token in the `X-Auth-CSRF` header (the server's requireAuthCsrf reads the
 * header when the body carries no `csrf`, so this works for every native + write
 * endpoint uniformly), and always includes credentials.
 *
 * Self-healing: a 403 can mean the cached token is stale because the underlying
 * PHP session was replaced — e.g. it was garbage-collected while a long-lived tab
 * sat idle, so the token no longer matches the fresh (empty-CSRF) session. We drop
 * the cache and retry once with a freshly minted token. A genuinely forbidden
 * request (real origin block, feature gate) simply 403s again and is returned as
 * is, at the cost of one extra round-trip in that rare case.
 */
export async function csrfFetch(
  url: string,
  opts: { method?: string; jsonBody?: unknown } = {},
): Promise<Response> {
  const method = opts.method ?? 'POST';
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { 'X-Auth-CSRF': await fetchAuthCsrf() };
    if (opts.jsonBody !== undefined) headers['Content-Type'] = 'application/json';
    return fetch(url, {
      method,
      credentials: 'include',
      headers,
      body: opts.jsonBody !== undefined ? JSON.stringify(opts.jsonBody) : undefined,
    });
  };
  const res = await send();
  if (res.status !== 403) return res;
  resetAuthCsrf();
  return send();
}

export interface NativeLoginResult {
  authenticated: boolean;
  user?: {
    sub: string;
    email: string | null;
    emailVerified: boolean;
    name: string | null;
    roles: string[];
    retailerId: string | null;
    retailerName: string | null;
  };
  /** Machine error code (e.g. 'invalid_credentials', 'rate_limited'). */
  error?: string;
  /** Human-readable message for display. */
  message?: string;
  /** When true, the client should complete login via the hosted flow. */
  fallback?: boolean;
  /** Reason for a fallback (e.g. 'account_setup_required'). */
  reason?: string;
  /** The credentials were valid but a forced password change is pending — show the set-password view. */
  passwordChangeRequired?: boolean;
  /**
   * The credentials were valid but the email was never verified. The server has
   * emailed a code — show the code view; auth-native-verify-code.php will confirm
   * the address and log them in. Never a hosted redirect: Keycloak has no mailer.
   */
  emailVerification?: boolean;
  /** Seconds the emailed code stays valid (with emailVerification). */
  expiresIn?: number;
  /** Set-password succeeded (used when auto-login afterwards couldn't complete). */
  passwordChanged?: boolean;
}

/**
 * Native password login (ROPC via the PHP BFF). Returns the parsed JSON for
 * every outcome (success, invalid creds, rate-limited, fallback) so the caller
 * can branch. Throws only on a network/parse failure.
 */
export async function nativeLogin(email: string, password: string): Promise<NativeLoginResult> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-login.php`, {
    method: 'POST',
    jsonBody: { email, password },
  });
  return (await res.json().catch(() => ({ authenticated: false }))) as NativeLoginResult;
}

/**
 * Native forced-password change (temp/expired password). Same BFF ROPC model as
 * nativeLogin — returns the parsed JSON for every outcome so the caller can
 * branch (success establishes the session; fallback finishes on the hosted flow).
 */
export async function nativeSetPassword(
  email: string,
  currentPassword: string,
  newPassword: string,
): Promise<NativeLoginResult> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-set-password.php`, {
    method: 'POST',
    jsonBody: { email, currentPassword, newPassword },
  });
  return (await res.json().catch(() => ({ authenticated: false }))) as NativeLoginResult;
}

export interface NativeActionResult {
  ok?: boolean;
  status?: string;
  error?: string;
  message?: string;
  /** Seconds the emailed verification code stays valid (code_sent responses). */
  expiresIn?: number;
}

/**
 * Native signup → is this email already registered? Fails OPEN (returns false)
 * on any error so it can never block signup; the server-side create is the real
 * gate. Returns { exists, checked } — `checked` is false when the check couldn't
 * actually run (so the UI can stay silent rather than imply "available").
 */
export async function checkEmailExists(email: string): Promise<{ exists: boolean; checked: boolean }> {
  try {
    const res = await csrfFetch(`${API_URL}/api/auth-native-check-email.php`, {
      method: 'POST',
      jsonBody: { email },
    });
    const data = await res.json().catch(() => ({}));
    return { exists: !!data.exists, checked: !!data.checked };
  } catch {
    return { exists: false, checked: false };
  }
}

/**
 * Native signup → emails a verification code. The Keycloak account isn't created
 * until the code is confirmed (nativeVerifyCode), so an abandoned signup leaves
 * nothing behind.
 *
 * Throws on a transport failure so callers can distinguish "couldn't ask" from
 * "asked, and the server said no" — silently treating a dead network as success
 * would tell the user an email is on its way when none was ever sent.
 */
export async function nativeSignup(fields: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}): Promise<NativeActionResult> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-signup.php`, {
    method: 'POST',
    jsonBody: fields,
  });
  return (await res.json().catch(() => ({ error: 'network' }))) as NativeActionResult;
}

export interface VerifyCodeResult {
  authenticated?: boolean;
  user?: NativeLoginResult['user'];
  ok?: boolean;
  error?: string;
  message?: string;
  attemptsLeft?: number;
}

/**
 * Native signup code flow: submit the emailed code. On the correct code the
 * server creates the (verified) account and logs the user in, returning the
 * authenticated payload; otherwise returns an error (incorrect/expired/too_many).
 */
export async function nativeVerifyCode(code: string): Promise<VerifyCodeResult> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-verify-code.php`, {
    method: 'POST',
    jsonBody: { code },
  });
  return (await res.json().catch(() => ({ ok: false, error: 'network' }))) as VerifyCodeResult;
}

/** Native signup code flow: regenerate + re-email the code (uses the session's pending signup). */
export async function nativeResendCode(): Promise<NativeActionResult> {
  // Empty object, not no-body: the endpoint rejects a non-JSON body with 400.
  const res = await csrfFetch(`${API_URL}/api/auth-native-resend-code.php`, { method: 'POST', jsonBody: {} });
  return (await res.json().catch(() => ({ error: 'network' }))) as NativeActionResult;
}

/**
 * Native password reset — request a code (also used to resend). Returns
 * status 'code_sent' (in-app code flow) or 'reset_sent' (Keycloak link fallback
 * when SMTP is off). Generic regardless of whether the account exists.
 */
export async function nativeResetRequest(email: string): Promise<NativeActionResult> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-reset-request.php`, {
    method: 'POST',
    jsonBody: { email },
  });
  return (await res.json().catch(() => ({ error: 'network' }))) as NativeActionResult;
}

/** Native password reset — verify the emailed code (marks the session ready to set a new password). */
export async function nativeResetVerify(code: string): Promise<NativeActionResult & { attemptsLeft?: number }> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-reset-verify.php`, {
    method: 'POST',
    jsonBody: { code },
  });
  return (await res.json().catch(() => ({ ok: false, error: 'network' }))) as NativeActionResult & { attemptsLeft?: number };
}

/** Native password reset — set the new password (after the code is verified) and log in. */
export async function nativeResetComplete(newPassword: string): Promise<VerifyCodeResult> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-reset-complete.php`, {
    method: 'POST',
    jsonBody: { newPassword },
  });
  return (await res.json().catch(() => ({ ok: false, error: 'network' }))) as VerifyCodeResult;
}

// ---------------------------------------------------------------------------
// Account "Sign-in & security": change/set password + linked social accounts.
// ---------------------------------------------------------------------------

export interface ChangePasswordResult {
  ok?: boolean;
  hasPassword?: boolean;
  error?: string;
  message?: string;
}

/**
 * Change (or, for a social-only account, set) the signed-in user's password.
 * Pass currentPassword='' when the account has no password yet.
 */
export async function nativeChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  const res = await csrfFetch(`${API_URL}/api/auth-native-change-password.php`, {
    method: 'POST',
    jsonBody: { currentPassword, newPassword },
  });
  return (await res.json().catch(() => ({ error: 'network' }))) as ChangePasswordResult;
}

export interface LinkedIdentity {
  provider: string;
  userName: string;
}
export interface LinkedAccountsInfo {
  manageable: boolean;
  /** Microsoft 365-managed staff identity: the whole tab is read-only. */
  staff?: boolean;
  hasPassword: boolean;
  linked: LinkedIdentity[];
  error?: string;
  message?: string;
}

/** The signed-in user's sign-in methods (password + linked social providers). */
export async function fetchLinkedAccounts(): Promise<LinkedAccountsInfo> {
  const res = await fetch(`${API_URL}/api/auth-linked-accounts.php`, { credentials: 'include' });
  return (await res.json().catch(() => ({ manageable: false, hasPassword: false, linked: [] }))) as LinkedAccountsInfo;
}

/** Unlink a social provider. Server refuses to remove the last sign-in method. */
export async function unlinkAccount(provider: string): Promise<LinkedAccountsInfo> {
  const res = await csrfFetch(`${API_URL}/api/auth-linked-accounts.php`, {
    method: 'DELETE',
    jsonBody: { provider },
  });
  return (await res.json().catch(() => ({ manageable: false, hasPassword: false, linked: [], error: 'network' }))) as LinkedAccountsInfo;
}

// ---------------------------------------------------------------------------
// SMTP / email settings (super-admin only) — dedicated endpoint so creds never
// touch the public settings.php GET. Password is write-only (surfaced as a bool).
// ---------------------------------------------------------------------------

export interface SmtpSettings {
  smtpEnabled: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpFrom: string;
  smtpFromName: string;
  smtpStartTls: boolean;
  smtpReplyTo: string;
  smtpNoReplyNotice: string;
  smtpPasswordSet: boolean;
  smtpConfigured: boolean;
}

export type SmtpSettingsPatch = Partial<Omit<SmtpSettings, 'smtpPasswordSet' | 'smtpConfigured'>> & { smtpPassword?: string };

export async function fetchSmtpSettings(): Promise<SmtpSettings> {
  const res = await fetch(`${API_URL}/api/admin-smtp.php`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to load SMTP settings');
  return data.settings as SmtpSettings;
}

export async function saveSmtpSettings(patch: SmtpSettingsPatch): Promise<SmtpSettings> {
  const res = await fetch(`${API_URL}/api/admin-smtp.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to save SMTP settings');
  return data.settings as SmtpSettings;
}

/** Send a test email using the saved config with the given field overrides applied. */
export async function sendSmtpTest(testTo: string, overrides: SmtpSettingsPatch): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${API_URL}/api/admin-smtp.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'test', testTo, settings: overrides }),
  });
  return (await res.json().catch(() => ({ ok: false, error: 'network' }))) as { ok: boolean; error?: string };
}

/**
 * Patch per-estimate admin toggles (e.g. MSRP visibility). Whitelist-driven
 * on the server — unknown keys are ignored. Returns the fresh admin-facing
 * view of the row.
 */
export interface EstimateAdminPatch {
  showMsrpToCreator?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  estimateName?: string | null;
  /** Admin recovery: clear the soft-delete flag so the estimate is live again. */
  restore?: boolean;
}

export interface EstimateAdminView {
  id: number;
  showMsrpToCreator: boolean;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  estimateName: string | null;
  isDeleted: boolean;
}

export async function updateEstimateAdmin(
  id: number,
  patch: EstimateAdminPatch,
): Promise<EstimateAdminView> {
  const response = await fetch(`${API_URL}/api/admin-estimates.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `Failed to update estimate (${response.status})`);
  }
  return data.estimate as EstimateAdminView;
}

// ============================================================================
// COMMENT ENDPOINTS
// ============================================================================

/**
 * Fetch all comments for an estimate by its GUID.
 * Returns a flat list ordered by creation date.
 */
export async function fetchComments(guid: string): Promise<Comment[]> {
  const response = await fetch(`${API_URL}/api/comments.php?guid=${encodeURIComponent(guid)}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to load comments');
  }
  return data.comments as Comment[];
}

/**
 * Post a new comment on an estimate.
 */
export async function postComment(payload: {
  guid: string;
  parentId?: number | null;
  guestName: string;
  commentText: string;
  turnstileToken: string;
  website?: string; // honeypot
}): Promise<Comment> {
  const response = await fetch(`${API_URL}/api/comments.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to post comment');
  }
  return data.comment as Comment;
}

// ============================================================================
// ESTIMATE ENDPOINTS (continued)
// ============================================================================

/**
 * Load a saved estimate by its GUID.
 *
 * @param guid - The UUID v4 identifier for the estimate
 * @returns The saved estimate data including wizard state and output
 */
/** Error thrown by loadEstimateByGuid when the estimate has been soft-deleted. */
export interface DeletedEstimateError extends Error {
  deleted: true;
}

export async function loadEstimateByGuid(guid: string, source?: string, recipient?: string): Promise<SavedEstimate> {
  const params = new URLSearchParams({ guid });
  if (source) params.set('source', source);
  if (recipient) params.set('recipient', recipient);
  const response = await fetch(`${API_URL}/api/estimates.php?${params}`);

  const data = await response.json();

  if (!response.ok) {
    // A soft-deleted estimate returns 410 with { deleted: true }; surface that
    // as a flag on the error so the shared view can show its dedicated state.
    const err = new Error(data.error || 'Estimate not found') as Error & { deleted?: boolean };
    if (data?.deleted) err.deleted = true;
    throw err;
  }

  return data;
}

/**
 * Persist a browser-recomputed snapshot back onto a stale estimate (same GUID,
 * same URL). The server re-verifies the estimate is actually due for a reprice
 * before writing, archives the outgoing snapshot to price history, and resets
 * the 30-day window. Best-effort: callers fire-and-forget and ignore failures
 * (the fresh pricing is already on screen regardless).
 */
export async function repriceEstimate(
  guid: string,
  estimateOutput: unknown,
): Promise<{ repriced: boolean; pricedAt?: string }> {
  const response = await fetch(`${API_URL}/api/estimates.php?action=reprice&guid=${encodeURIComponent(guid)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ estimateOutput }),
  });
  if (!response.ok) throw new Error(`Reprice failed: ${response.status}`);
  return response.json();
}

// ============================================================================
// GUEST-ESTIMATE CLAIMING (requires a logged-in, email-verified user)
// ============================================================================

export interface ClaimableEstimate {
  guid: string;
  estimateName: string | null;
  projectType: string | null;
  city: string | null;
  region: string | null;
  createdAt: string;
}

/** List the current user's claimable guest estimates (matched by verified email). */
export async function fetchClaimableEstimates(): Promise<ClaimableEstimate[]> {
  const res = await fetch(`${API_URL}/api/auth-claim-estimates.php`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Claimable lookup failed: ${res.status}`);
  const data = await res.json();
  return (data.claimable ?? []) as ClaimableEstimate[];
}

/** Attach guest estimates to the logged-in user. Omit `guids` to claim all. */
export async function claimEstimates(guids?: string[]): Promise<number> {
  const res = await csrfFetch(`${API_URL}/api/auth-claim-estimates.php`, {
    method: 'POST',
    jsonBody: guids ? { guids } : {},
  });
  if (!res.ok) throw new Error(`Claim failed: ${res.status}`);
  const data = await res.json();
  return (data.claimed ?? 0) as number;
}

// ============================================================================
// AUTHENTICATED ESTIMATE SAVE / RENAME (logged-in users, no lead form)
// ============================================================================

export interface AuthEstimateSaveInput {
  estimateName: string;
  estimateData: unknown;
  wizardState: unknown;
  retailerId?: number | null;
  /** Lead-quality fields captured on the save modal (per-estimate). */
  projectType?: string;
  userCategory?: string;
  country?: string;
  region?: string;
  city?: string;
  postalCode?: string;
}

/** Create an estimate owned by the logged-in user. Returns the new guid. */
export async function saveAuthEstimate(input: AuthEstimateSaveInput): Promise<{ guid: string; estimateName: string } & RenderSaveExtras> {
  const res = await csrfFetch(`${API_URL}/api/auth-estimates.php`, {
    method: 'POST',
    jsonBody: {
      estimateName: input.estimateName,
      estimateData: input.estimateData,
      wizardState: input.wizardState,
      retailerId: input.retailerId ?? null,
      projectType: input.projectType ?? '',
      userCategory: input.userCategory ?? '',
      country: input.country ?? '',
      region: input.region ?? '',
      city: input.city ?? '',
      postalCode: input.postalCode ?? '',
      // As in submitGuestEstimate: the language the estimate was built in, so a
      // shared link opens in that language rather than the recipient's default.
      locale: activeLocale(),
    },
  });
  if (!res.ok) throw new Error(`Save failed: ${res.status}`);
  return res.json();
}

// ============================================================================
// MY ESTIMATES (logged-in users — list + soft-delete their own estimates)
// ============================================================================

export interface MyEstimate {
  guid: string;
  estimateName: string | null;
  projectType: string | null;
  status: string;
  calculationMode: string | null;
  spaceType: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  views: number;
  comments: number;
  createdAt: string;
  updatedAt: string;
}

/** List the logged-in user's own saved estimates (excludes soft-deleted). */
export async function fetchMyEstimates(): Promise<MyEstimate[]> {
  const res = await fetch(`${API_URL}/api/auth-estimates.php`, { credentials: 'include' });
  if (!res.ok) throw new Error(`My estimates lookup failed: ${res.status}`);
  const data = await res.json();
  return (data.estimates ?? []) as MyEstimate[];
}

/** Soft-delete an estimate the user owns. Idempotent server-side. */
export async function deleteMyEstimate(guid: string): Promise<void> {
  const res = await csrfFetch(`${API_URL}/api/auth-estimates.php?guid=${encodeURIComponent(guid)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
}

// ============================================================================
// USER PROFILE (logged-in users; phone + I-Am-A + default location)
// ============================================================================

export interface UserProfile {
  phone: string;
  userCategory: string;
  country: string;
  region: string;
  city: string;
  postalCode: string;
}

export async function fetchUserProfile(): Promise<UserProfile> {
  const res = await fetch(`${API_URL}/api/auth-profile.php`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Profile load failed: ${res.status}`);
  const data = await res.json();
  return (data.profile ?? {}) as UserProfile;
}

/** Save a partial profile patch; returns the merged profile. */
export async function saveUserProfile(patch: Partial<UserProfile>): Promise<UserProfile> {
  const res = await csrfFetch(`${API_URL}/api/auth-profile.php`, {
    method: 'POST',
    jsonBody: patch,
  });
  if (!res.ok) throw new Error(`Profile save failed: ${res.status}`);
  const data = await res.json();
  return (data.profile ?? {}) as UserProfile;
}

/** Rename an estimate the logged-in user owns (optionally updating project type). */
export async function renameAuthEstimate(guid: string, estimateName: string, projectType?: string): Promise<void> {
  const body: Record<string, unknown> = { guid, estimateName };
  if (projectType !== undefined) body.projectType = projectType;
  const res = await csrfFetch(`${API_URL}/api/auth-estimates.php`, {
    method: 'PATCH',
    jsonBody: body,
  });
  if (!res.ok) throw new Error(`Rename failed: ${res.status}`);
}

/**
 * Background auto-save of the latest config + result onto an owned estimate.
 * `estimateData` is omitted while mid-edit (no result) so the server keeps the
 * last good output rather than nulling it.
 */
export async function patchAuthEstimateData(
  guid: string,
  data: { estimateData?: unknown; wizardState: unknown },
): Promise<void> {
  const body: Record<string, unknown> = { guid, wizardState: data.wizardState };
  if (data.estimateData !== undefined && data.estimateData !== null) {
    body.estimateData = data.estimateData;
  }
  const res = await csrfFetch(`${API_URL}/api/auth-estimates.php`, {
    method: 'PATCH',
    jsonBody: body,
  });
  if (!res.ok) throw new Error(`Auto-save failed: ${res.status}`);
}
